import { useCallback, useEffect, useReducer, useState } from 'react'
import { ServicePlanModel, SearchResponse } from '@user/store/map/typings'
import { ServiceConfig } from '@sales-center/common'
import { getNoRedirect } from '@user/store/services/crud'

interface UseServiceHistoryOptions {
  locationId: string | null | undefined
  enabled?: boolean
}

interface UseServiceHistoryReturn {
  services: ServicePlanModel[]
  loading: boolean
  error: string | null
  page: number
  setPage: (page: number) => void
  total: number
  pageSize: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  fetchServices: () => Promise<void>
}

interface FetchState {
  services: ServicePlanModel[]
  loading: boolean
  error: string | null
  total: number
}

type FetchAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; services: ServicePlanModel[]; total: number }
  | { type: 'FETCH_ERROR'; error: string }

const initialFetchState: FetchState = {
  services: [],
  loading: false,
  error: null,
  total: 0,
}

function fetchReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null }
    case 'FETCH_SUCCESS':
      return {
        services: action.services,
        total: action.total,
        loading: false,
        error: null,
      }
    case 'FETCH_ERROR':
      return { services: [], total: 0, loading: false, error: action.error }
    default:
      return state
  }
}

export function useServiceHistory({
  locationId,
  enabled = true,
}: UseServiceHistoryOptions): UseServiceHistoryReturn {
  const [{ services, loading, error, total }, dispatch] = useReducer(
    fetchReducer,
    initialFetchState,
  )
  const [page, setPage] = useState(0)
  const pageSize = 5

  const fetchServices = useCallback(async () => {
    if (!locationId || !enabled) {
      return
    }

    dispatch({ type: 'FETCH_START' })

    try {
      const skip = page * pageSize
      const url = `${ServiceConfig.baseAPIUrl}/Location/core/${locationId}/serviceplans?skip=${skip}&take=${pageSize}`
      const response = await getNoRedirect(url)

      if (response.ok) {
        const data: SearchResponse<ServicePlanModel> = await response.json()
        dispatch({
          type: 'FETCH_SUCCESS',
          services: data.items || [],
          total: data.count || 0,
        })
      } else {
        dispatch({
          type: 'FETCH_ERROR',
          error: 'Failed to load service history.',
        })
      }
    } catch (err) {
      dispatch({
        type: 'FETCH_ERROR',
        error:
          err instanceof Error
            ? err.message
            : 'Failed to load service history.',
      })
    }
  }, [locationId, page, pageSize, enabled])

  useEffect(() => {
    fetchServices()
  }, [fetchServices])

  const hasNextPage = (page + 1) * pageSize < total
  const hasPreviousPage = page > 0

  return {
    services,
    loading,
    error,
    page,
    setPage,
    total,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    fetchServices,
  }
}
